import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import {
  ChargingStationLocationDefaultsRequest,
  ChargingStationLocationDetails,
  DYNAMIC_PRICE_POLICY_NET_AREA_MESSAGE,
} from '../charging-station-location-model';
import { ChargingStationLocationRelationService } from 'src/app/shared/services/charging-station-location-relation.service';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { PageEvent } from '@angular/material/paginator';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroup, AccessGroupSearchEvent, DefaultAccessGroup } from '../../access-groups/access-group.model';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ReactiveFormsModule } from '@angular/forms';
import { AdmPricePolicyTypeaheadComponent } from 'src/app/shared/components/adm-price-policy-typeahead/adm-price-policy-typeahead.component';
import { PricePolicyType, PricePolicy } from '../../prices/price-policies/interfaces/price-policy';
import { HttpErrorResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';

interface ChargingStationLocationDefaultsForm {
  chargingStationLocationRelationId: FormControl<number | null>;
  pricePolicyId: FormControl<number | null>;
  selectedDefaultAccessGroups: FormControl<DefaultAccessGroup[]>;
}

export interface ChargingStationLocationDefaultsDialogData {
  locationIdentifier: string;
  organizationId: number;
  canHaveDynamicPricePolicy: boolean;
}

@Component({
  selector: 'adm-charging-station-location-defaults-modal',
  templateUrl: './charging-station-location-defaults-modal.component.html',
  styleUrls: ['./charging-station-location-defaults-modal.component.scss'],
  imports: [
    LoadingSpinnerComponent,
    CustomDropDownArrowDirective,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    AdmPricePolicyTypeaheadComponent,
    FeedbackInfoComponent,
  ],
})
export class ChargingStationLocationDefaultsModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ChargingStationLocationDefaultsModalComponent>>(MatDialogRef);
  data = inject<ChargingStationLocationDefaultsDialogData>(MAT_DIALOG_DATA);
  private chargingStationLocationService = inject(ChargingStationLocationService);
  private chargingStationLocationRelationService = inject(ChargingStationLocationRelationService);
  private snackbarService = inject(SnackbarService);
  private accessGroupService = inject(AccessGroupService);

  private readonly validateNetAreaForPricePolicy = toSignal(
    inject(FeatureFlagService)
      .getFeatureFlag(FEATURE_FLAGS.VALIDATE_NET_AREA_FOR_PRICE_POLICY)
      .pipe(map(flag => flag.result)),
    { initialValue: false }
  );

  public readonly dynamicPricePolicyWarning = DYNAMIC_PRICE_POLICY_NET_AREA_MESSAGE;
  public selectedPricePolicy: PricePolicy | null = null;
  public initialPricePolicyId: number | null = null;
  public accessGroups: Array<AccessGroup> = [];
  public saving = false;
  public loading = signal<boolean>(false);
  public chargingStationLocation: ChargingStationLocationDetails;
  public pageEvent: PageEvent = new PageEvent();
  public selectedPricePolicyName: string | null = null;
  public accessGroupSearchEvent: AccessGroupSearchEvent = new AccessGroupSearchEvent(this.pageEvent);

  public chargingStationLocationDefaultsFormGroup: FormGroup<ChargingStationLocationDefaultsForm> =
    new FormGroup<ChargingStationLocationDefaultsForm>({
      chargingStationLocationRelationId: new FormControl<number | null>(null, Validators.required),
      pricePolicyId: new FormControl<number | null>(null, Validators.required),
      selectedDefaultAccessGroups: new FormControl<DefaultAccessGroup[]>([], { nonNullable: true }),
    });

  get canHaveDynamicPricePolicy(): boolean {
    return !this.validateNetAreaForPricePolicy() || this.data.canHaveDynamicPricePolicy === true;
  }

  get chargingStationLocationRelationId(): FormControl<number | null> {
    return this.chargingStationLocationDefaultsFormGroup.controls.chargingStationLocationRelationId;
  }

  get pricePolicyId(): FormControl<number | null> {
    return this.chargingStationLocationDefaultsFormGroup.controls.pricePolicyId;
  }

  get pricePolicyType(): PricePolicyType {
    return this.chargingStationLocation?.hasOkPowerMeter ? PricePolicyType.FixedWithOkMeter : PricePolicyType.FixedWithoutOkMeter;
  }

  get selectedDefaultAccessGroups(): FormControl<DefaultAccessGroup[]> {
    return this.chargingStationLocationDefaultsFormGroup.controls.selectedDefaultAccessGroups;
  }

  ngOnInit() {
    this.accessGroupSearchEvent.organizationId = this.data.organizationId;

    this.chargingStationLocationService
      .getChargingStationLocationDetails(this.data.locationIdentifier)
      .pipe(
        switchMap(chargingStationLocation => {
          this.chargingStationLocation = chargingStationLocation;
          this.selectedPricePolicyName = chargingStationLocation?.defaultPricePolicy?.name ?? null;

          if (!chargingStationLocation) {
            return of({ defaults: null, accessGroups: [] as AccessGroup[] });
          }

          const chargingStationLocationRelationId = chargingStationLocation.chargingStationLocationRelationId;
          this.chargingStationLocationRelationId.setValue(chargingStationLocationRelationId);

          return forkJoin({
            defaults: this.chargingStationLocationRelationService.getDefaults(chargingStationLocationRelationId).pipe(
              catchError(err => {
                console.warn('err', err);
                return of(null);
              })
            ),
            accessGroups: this.accessGroupService
              .getAccessGroups(this.accessGroupSearchEvent)
              .pipe(map(accessGroups => accessGroups.items)),
          });
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: ({ defaults, accessGroups }) => {
          this.pricePolicyId.setValue(defaults?.pricePolicyId ?? null);
          this.initialPricePolicyId = defaults?.pricePolicyId ?? null;
          this.selectedDefaultAccessGroups.setValue(this.chargingStationLocation?.defaultAccessGroups ?? []);
          this.accessGroups = accessGroups;
        },
        error: err => {
          console.warn('err', err);
        },
      });
  }
  compareObjects(ag1: DefaultAccessGroup, ag2: DefaultAccessGroup): boolean {
    return ag1?.identifier === ag2?.identifier;
  }

  onPricePolicySelected(pricePolicy: PricePolicy | null): void {
    this.selectedPricePolicy = pricePolicy;
    this.pricePolicyId.setValue(pricePolicy?.id ?? null);
    this.selectedPricePolicyName = pricePolicy?.name ?? this.selectedPricePolicyName;
  }

  public async saveAndClose() {
    if (!this.canHaveDynamicPricePolicy && this.selectedPricePolicy?.type === PricePolicyType.Dynamic) {
      this.snackbarService.warning(this.dynamicPricePolicyWarning);
      return;
    }

    if (this.chargingStationLocationDefaultsFormGroup.invalid) {
      return;
    }

    this.saving = true;

    const accessGroupIdentifiers: string[] = [];
    this.selectedDefaultAccessGroups.value.forEach(accessGroup => {
      accessGroupIdentifiers.push(accessGroup.identifier);
    });

    const chargingStationLocationRelationId = this.chargingStationLocationRelationId.value;
    if (chargingStationLocationRelationId == null) {
      this.saving = false;
      return;
    }
    const selectedPricePolicyId = this.pricePolicyId.value;
    if (selectedPricePolicyId == null) {
      this.saving = false;
      return;
    }

    const chargingStationLocationDefaultsRequest = new ChargingStationLocationDefaultsRequest(
      selectedPricePolicyId,
      accessGroupIdentifiers
    );
    this.chargingStationLocationRelationService
      .upsertChargingStationLocationDefaults(chargingStationLocationRelationId, chargingStationLocationDefaultsRequest)
      .subscribe({
        next: () => {
          this.snackbarService.success('Lokationen blev succesfuldt redigeret.');
          this.saving = false;

          // Get the updated access groups
          const updatedAccessGroups = this.selectedDefaultAccessGroups.value;

          // Return the updated data along with the identifier
          this.dialogRef.close({
            chargingStationLocationIdentifier: this.chargingStationLocation.chargingStationLocationIdentifier,
            defaultPricePolicy: {
              id: selectedPricePolicyId,
              name: this.selectedPricePolicyName ?? '',
            },
            defaultAccessGroups: updatedAccessGroups,
          });
        },
        error: (err: HttpErrorResponse) => {
          if (err.error?.code === 100613) {
            this.snackbarService.error(
              'Den valgte prispolitik passer ikke til den valgte OK-måler-indstilling. Vælg en anden prispolitik og prøv igen.'
            );
          } else if (err.error?.code === 100620) {
            this.snackbarService.error(this.dynamicPricePolicyWarning);
          } else if (err.error?.code === 100606) {
            this.snackbarService.error('Lokationen kunne ikke findes og ændringerne er således ikke blevet gemt.');
          } else {
            this.snackbarService.error('Der skete en ukendt fejl under redigering');
          }
        },
      })
      .add(() => {
        this.saving = false;
      });
  }
}
